import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usageApi } from '../api/usage';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { ShoppingBag, Calendar, Clock, Search, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const UsageHistory = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // Debounce для пошуку
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Конвертуємо sortOption в параметри для бекенду
  const sortBy = useMemo(() => {
    switch (sortOption) {
      case 'newest':
      case 'oldest':
        return 'usedAt';
      case 'highestSaving':
      case 'lowestSaving':
        return 'savedAmount';
      default:
        return 'usedAt';
    }
  }, [sortOption]);
  
  const sortDirection = useMemo(() => {
    switch (sortOption) {
      case 'newest':
      case 'highestSaving':
        return 'desc';
      case 'oldest':
      case 'lowestSaving':
        return 'asc';
      default:
        return 'desc';
    }
  }, [sortOption]);
  
  const { data: usageData, isLoading, isError } = useQuery({
    queryKey: ['userUsageHistory', 'paginated', user?.id, currentPage, pageSize, sortBy, sortDirection],
    queryFn: () => usageApi.getUserUsagesPaginated(user?.id, {
      page: currentPage,
      size: pageSize,
      sortBy,
      sortDirection,
    }).then(res => res.data),
    enabled: !!user?.id,
    keepPreviousData: true,
  });
  
  const { data: savingsData } = useQuery({
    queryKey: ['userSavings', user?.id],
    queryFn: () => usageApi.getTotalSavingsByUser(user?.id).then(res => res.data),
    enabled: !!user?.id,
  });
  
  // Фільтрація на клієнті для пошуку (бо бекенд не підтримує пошук по назві знижки/партнера)
  const filteredHistory = useMemo(() => {
    const history = usageData?.content || [];
    if (!debouncedSearchTerm) return history;
    
    return history.filter(usage => 
      usage.discountTitle?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      usage.partnerName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [usageData?.content, debouncedSearchTerm]);
  
  const totalPages = usageData?.totalPages || 0;
  const totalElements = usageData?.totalElements || 0;
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Історія використання</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Левая колонка - статистика */}
        <div className="lg:col-span-1">
          <Card title="Загальна статистика">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <h3 className="text-green-800 font-medium mb-1">Заощаджено</h3>
                <p className="text-2xl font-bold text-green-600">
                  {savingsData?.totalSavings ? `${savingsData.totalSavings} грн` : '0 грн'}
                </p>
              </div>
              
              <div className="text-center">
                <h3 className="text-gray-700 font-medium mb-1">Всього використань</h3>
                <p className="text-xl font-semibold text-gray-800">
                  {totalElements || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Правая колонка - история */}
        <div className="lg:col-span-3">
          <Card>
            <div className="mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder="Пошук за назвою знижки або партнера..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
                
                <div className="min-w-[150px]">
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  >
                    <option value="newest">Найновіші спочатку</option>
                    <option value="oldest">Найстаріші спочатку</option>
                    <option value="highestSaving">Найбільші заощадження</option>
                    <option value="lowestSaving">Найменші заощадження</option>
                  </select>
                </div>
              </div>
            </div>
            
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse flex items-start">
                    <div className="w-12 h-12 bg-gray-200 rounded-full mr-3"></div>
                    <div className="flex-grow">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <AlertCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-red-800">Помилка завантаження</h3>
                  <p className="text-red-700">Не вдалося завантажити історію використання. Спробуйте оновити сторінку.</p>
                </div>
              </div>
            ) : filteredHistory.length === 0 ? (
              searchTerm ? (
                <div className="text-center py-8">
                  <Search size={36} className="mx-auto text-gray-400 mb-2" />
                  <h3 className="text-lg font-medium text-gray-800 mb-1">Нічого не знайдено</h3>
                  <p className="text-gray-600">
                    Спробуйте змінити умови пошуку або перегляньте всю історію
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingBag size={36} className="mx-auto text-gray-400 mb-2" />
                  <h3 className="text-lg font-medium text-gray-800 mb-1">Немає історії використання</h3>
                  <p className="text-gray-600">
                    Ви ще не використовували жодної знижки. Перегляньте доступні знижки в каталозі.
                  </p>
                  <Button 
                    to="/discounts" 
                    variant="primary"
                    className="mt-4"
                  >
                    Перейти до каталогу
                  </Button>
                </div>
              )
            ) : (
              <>
                <div className="space-y-6">
                  {filteredHistory.map(usage => (
                  <div key={usage.id} className="border-b border-gray-100 pb-6 last:border-0">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <ShoppingBag size={20} className="text-primary-600" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-1">
                          <h3 className="font-semibold text-lg">{usage.discountTitle}</h3>
                          <div className="flex items-center text-gray-500">
                            <Calendar size={14} className="mr-1" />
                            <span className="text-sm">
                              {format(new Date(usage.usedAt), 'dd MMMM yyyy', { locale: uk })}
                            </span>
                          </div>
                        </div>
                        <p className="text-primary-600 font-medium">{usage.partnerName}</p>
                        
                        <div className="mt-2 flex flex-wrap gap-2">
                          <div className="bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-700 flex items-center">
                            <Clock size={14} className="mr-1" />
                            <span>
                              {format(new Date(usage.usedAt), 'HH:mm', { locale: uk })}
                            </span>
                          </div>
                          
                          {usage.savedAmount && (
                            <div className="bg-green-100 rounded-full px-3 py-1 text-sm text-green-700 flex items-center">
                              <span>Заощаджено: {usage.savedAmount} грн</span>
                            </div>
                          )}
                        </div>
                        
                        {usage.notes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <p className="italic">{usage.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
                
                {/* Пагінація */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <div className="text-sm text-gray-700">
                      Показано <span className="font-medium">{currentPage * pageSize + 1}</span> до{' '}
                      <span className="font-medium">
                        {Math.min((currentPage + 1) * pageSize, totalElements)}
                      </span> з{' '}
                      <span className="font-medium">{totalElements}</span> записів
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                        className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      
                      <span className="text-sm text-gray-700">
                        Сторінка {currentPage + 1} з {totalPages}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={currentPage >= totalPages - 1}
                        className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UsageHistory;